<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import VueMarkdown from "vue-markdown-render";
import { useHelpPanelStore } from "./helpPanelStore.ts";
import { helpSections } from "./helpTopics.ts";

// Mounted once in app.vue. A slide-over rather than a modalStore dialog: the
// help is meant to be read alongside the page it describes, not centred over it.
const helpPanel = useHelpPanelStore();

const panelRef = ref<HTMLElement | null>(null);
const headingRef = ref<HTMLElement | null>(null);
let triggerBeforeOpen: HTMLElement | null = null;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableElements(): HTMLElement[] {
  const panel = panelRef.value;
  if (!panel) {
    return [];
  }
  return [...panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)];
}

// Escape on the capture phase so a focused control inside cannot swallow it,
// matching ModalContainer.
function onKeyDown(event: KeyboardEvent): void {
  if (!helpPanel.isOpen) {
    return;
  }

  if (event.key === "Escape") {
    event.preventDefault();
    helpPanel.close();
    return;
  }

  if (event.key !== "Tab") {
    return;
  }

  const focusable = focusableElements();
  const first = focusable[0];
  const last = focusable.at(-1);
  if (!first || !last) {
    return;
  }

  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === panelRef.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

watch(
  () => helpPanel.isOpen,
  (isOpen) => {
    if (isOpen) {
      triggerBeforeOpen =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      void nextTick(() => {
        headingRef.value?.focus();
      });
      return;
    }

    // Returning focus to the trigger keeps keyboard users where they were.
    triggerBeforeOpen?.focus();
    triggerBeforeOpen = null;
  }
);

onMounted(() => {
  document.addEventListener("keydown", onKeyDown, true);
});

onUnmounted(() => {
  document.removeEventListener("keydown", onKeyDown, true);
});
</script>

<template>
  <Teleport to="body">
    <Transition name="help-slide-over">
      <div v-if="helpPanel.isOpen" class="help-slide-over-root">
        <button
          type="button"
          class="help-slide-over-scrim"
          aria-label="Close help"
          @click="helpPanel.close()"
        />

        <aside
          ref="panelRef"
          class="help-slide-over-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="help-slide-over-heading"
        >
          <header class="help-slide-over-header">
            <div class="min-w-0">
              <p class="help-slide-over-eyebrow">Help</p>
              <h2
                id="help-slide-over-heading"
                ref="headingRef"
                class="help-slide-over-title"
                tabindex="-1"
              >
                {{ helpPanel.activeTopic?.title ?? "Contents" }}
              </h2>
            </div>
            <button
              type="button"
              class="help-slide-over-close"
              aria-label="Close help"
              @click="helpPanel.close()"
            >
              <span aria-hidden="true">✕</span>
            </button>
          </header>

          <div class="help-slide-over-body">
            <template v-if="helpPanel.activeTopic">
              <button
                type="button"
                class="help-slide-over-back"
                @click="helpPanel.showTableOfContents()"
              >
                ← All help topics
              </button>
              <div class="help-slide-over-markdown">
                <VueMarkdown :source="helpPanel.activeTopic.body" />
              </div>
            </template>

            <template v-else>
              <p class="help-slide-over-intro">
                Short guides to what each page does, where its numbers come
                from, and how redistribution is applied.
              </p>
              <nav
                v-for="section in helpSections"
                :key="section.key"
                class="help-slide-over-section"
                :aria-label="section.label"
              >
                <p class="help-slide-over-section-label">{{ section.label }}</p>
                <ul class="help-slide-over-topic-list">
                  <li v-for="topic in section.topics" :key="topic.id">
                    <button
                      type="button"
                      class="help-slide-over-topic"
                      @click="helpPanel.open(topic.id)"
                    >
                      <span class="help-slide-over-topic-title">
                        {{ topic.title }}
                      </span>
                      <span class="help-slide-over-topic-summary">
                        {{ topic.summary }}
                      </span>
                    </button>
                  </li>
                </ul>
              </nav>
            </template>
          </div>
        </aside>
      </div>
    </Transition>
  </Teleport>
</template>
